import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MessageCircle, Tag } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "../Reveal";
import { waLink } from "../../lib/constants";
import { useSettings } from "../../lib/SettingsContext";
import { ServiceIcon } from "../ServiceIcon";

const chipCls = (active) =>
  `px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 ${
    active
      ? "bg-yellow-400 text-black border-yellow-400 font-bold"
      : "bg-[#161618] text-zinc-300 border-[#28282D] hover:border-yellow-400/50"
  }`;

export const TireWizard = ({ services, onRequest }) => {
  const s = useSettings();
  const brands = s.car_brands || [];
  const sizes = s.tire_sizes || [];
  const [step, setStep] = useState(1);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [size, setSize] = useState("");
  const [service, setService] = useState("");

  const waMsg = `السلام عليكم، أحتاج عرض سعر من نجم الإطارات.\nالسيارة: ${brand}${model ? " " + model : ""}\nمقاس الإطار: ${size}\nالخدمة المطلوبة: ${service}\nموقعي:`;

  const steps = [
    { n: 1, label: "نوع السيارة" },
    { n: 2, label: "مقاس الإطار" },
    { n: 3, label: "الخدمة" },
    { n: 4, label: "اطلب السعر" },
  ];

  return (
    <section id="wizard" data-testid="wizard-section" className="py-24 bg-[#0d0d0f] border-y border-[#1c1c20]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            kicker="اطلب السعر"
            title="حدد سيارتك وخدمتك في دقيقة"
            desc="اختر نوع السيارة ومقاس الإطار والخدمة المطلوبة، وأرسل طلبك — نرد عليك بالسعر فورًا."
            align="center"
          />
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="tire-wizard" className="mt-12 bg-[#121214] border border-[#28282D] rounded-2xl p-6 sm:p-10">
            <div className="flex items-center justify-between mb-10">
              {steps.map((s, i) => (
                <div key={s.n} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5">
                    <span
                      data-testid={`wizard-step-${s.n}`}
                      className={`w-9 h-9 rounded-full border flex items-center justify-center font-display font-bold text-sm transition-colors duration-300 ${
                        step >= s.n ? "bg-yellow-400 text-black border-yellow-400" : "border-[#28282D] text-zinc-500"
                      }`}
                    >
                      {s.n}
                    </span>
                    <span className={`text-[11px] hidden sm:block ${step >= s.n ? "text-yellow-400" : "text-zinc-600"}`}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`h-px flex-1 mx-2 transition-colors duration-300 ${step > s.n ? "bg-yellow-400" : "bg-[#28282D]"}`} />
                  )}
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 24 }}
                transition={{ duration: 0.35, ease: EASE }}
              >
                {step === 1 && (
                  <div>
                    <div className="text-white font-display font-bold text-lg mb-5">ما نوع سيارتك؟</div>
                    <div className="flex flex-wrap gap-2.5">
                      {brands.map((b, i) => (
                        <button key={b} data-testid={`wizard-brand-${i}`} onClick={() => setBrand(b)} className={chipCls(brand === b)}>
                          {b}
                        </button>
                      ))}
                    </div>
                    {brand && (
                      <input
                        data-testid="wizard-model-input"
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        placeholder="موديل السيارة (اختياري) — مثال: كامري"
                        className="mt-5 w-full bg-[#0A0A0A] border border-[#28282D] rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-yellow-400 outline-none transition-colors"
                      />
                    )}
                    <div className="mt-8 flex justify-start">
                      <button
                        data-testid="wizard-next-1"
                        disabled={!brand}
                        onClick={() => setStep(2)}
                        className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-yellow-300 transition-colors"
                      >
                        التالي <ArrowLeft className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <div className="text-white font-display font-bold text-lg mb-5">ما مقاس الإطار؟</div>
                    <div className="flex flex-wrap gap-2.5">
                      {sizes.map((sz, i) => (
                        <button key={sz} data-testid={`wizard-size-${i}`} onClick={() => setSize(sz)} className={chipCls(size === sz)}>
                          <span dir="ltr">{sz}</span>
                        </button>
                      ))}
                    </div>
                    <div className="mt-8 flex gap-3">
                      <button data-testid="wizard-back-2" onClick={() => setStep(1)} className="flex items-center gap-2 border border-[#28282D] text-zinc-300 px-6 py-3 rounded-full hover:border-yellow-400/50 transition-colors">
                        <ArrowRight className="w-4 h-4" /> رجوع
                      </button>
                      <button
                        data-testid="wizard-next-2"
                        disabled={!size}
                        onClick={() => setStep(3)}
                        className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-yellow-300 transition-colors"
                      >
                        التالي <ArrowLeft className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <div className="text-white font-display font-bold text-lg mb-5">ما الخدمة المطلوبة؟</div>
                    <div className="flex flex-wrap gap-2.5">
                      {services.map((s) => (
                        <button key={s.id} data-testid={`wizard-service-${s.id}`} onClick={() => setService(s.title)} className={`${chipCls(service === s.title)} flex items-center gap-2`}>
                          <ServiceIcon name={s.icon} className="w-4 h-4" />
                          {s.title}
                        </button>
                      ))}
                    </div>
                    <div className="mt-8 flex gap-3">
                      <button data-testid="wizard-back-3" onClick={() => setStep(2)} className="flex items-center gap-2 border border-[#28282D] text-zinc-300 px-6 py-3 rounded-full hover:border-yellow-400/50 transition-colors">
                        <ArrowRight className="w-4 h-4" /> رجوع
                      </button>
                      <button
                        data-testid="wizard-next-3"
                        disabled={!service}
                        onClick={() => setStep(4)}
                        className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-yellow-300 transition-colors"
                      >
                        التالي <ArrowLeft className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <div className="text-white font-display font-bold text-lg mb-5">ملخص طلبك</div>
                    <div className="bg-[#0A0A0A] border border-yellow-400/30 rounded-xl p-5 space-y-2.5 text-sm">
                      <div className="flex justify-between"><span className="text-zinc-500">السيارة</span><span className="text-white font-semibold">{brand}{model ? ` — ${model}` : ""}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">مقاس الإطار</span><span className="text-white font-semibold" dir="ltr">{size}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">الخدمة</span><span className="text-yellow-400 font-semibold">{service}</span></div>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <button data-testid="wizard-back-4" onClick={() => setStep(3)} className="flex items-center gap-2 border border-[#28282D] text-zinc-300 px-6 py-3 rounded-full hover:border-yellow-400/50 transition-colors">
                        <ArrowRight className="w-4 h-4" /> رجوع
                      </button>
                      <button
                        data-testid="wizard-submit-btn"
                        onClick={() => onRequest({ service, car_brand: brand, car_model: model, tire_size: size })}
                        className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-300 transition-colors"
                      >
                        <Tag className="w-4 h-4" /> اطلب السعر الآن
                      </button>
                      <a
                        data-testid="wizard-whatsapp-btn"
                        href={waLink(waMsg, s.whatsapp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 border border-green-500/50 text-green-400 font-semibold px-6 py-3 rounded-full hover:bg-green-500/10 transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" /> واتساب مباشر
                      </a>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
